/**
 * EVAS v3 — IP-intelligence contracts (EVAS-136, Phase 1; research + design: docs/leadval/04).
 * Provider IMPLEMENTATIONS live in `packages/geoip` (Phase 3); these are the canonical shapes:
 * the result every provider normalizes to, the provider/preset config, and the global-vs-company
 * resolution rule. Geo and anonymity are SEPARATE provider axes on purpose — different best-in-class
 * vendors, licensing and failure modes (04 §9).
 */
import { z } from "zod";
// ---------- result ----------
/** Honest-accuracy reminder baked into the contract: country is reliable (~99%), region usually
 * right, city ≈ right metro ~2/3 of the time, `postal` is a HINT to display, never a fact to act on
 * (04 §1). A street address can never be resolved from an IP. */
export const GeoIpGeo = z.object({
    country: z.string().optional(), // ISO 3166-1 alpha-2
    region: z.string().optional(),
    city: z.string().optional(),
    postal: z.string().optional(),
    lat: z.number().optional(),
    lon: z.number().optional(),
    accuracyRadiusKm: z.number().optional(),
    timezone: z.string().optional(), // IANA
});
export const GeoIpNetwork = z.object({
    asn: z.number().optional(),
    asOrg: z.string().optional(),
    isp: z.string().optional(),
});
/** `relay` = iCloud Private Relay / Cloudflare-WARP-style relays — deliberately distinct from VPN.
 * `sources` carries per-verdict provenance ("provider claims" vs "Tor exit list", …) so downstream
 * consumers can weigh corroboration.
 *
 * ⚠ Every boolean DEFAULTS to `false`, so on its own an all-false verdict cannot tell "checked and clean"
 * from "nobody was asked". Read `vendorChecked` before treating a `false` as a measurement. */
export const GeoIpAnonymity = z.object({
    isAnonymous: z.boolean().default(false),
    isVpn: z.boolean().default(false),
    isProxy: z.boolean().default(false),
    isTor: z.boolean().default(false),
    isRelay: z.boolean().default(false),
    isHosting: z.boolean().default(false),
    isResidentialProxy: z.boolean().optional(), // only some providers can tell
    serviceName: z.string().optional(), // "NordVPN" — provider-name-capable tiers only
    confidence: z.number().optional(),
    riskScore: z.number().optional(),
    sources: z.array(z.string()).default([]),
    /**
     * `true` only when an anonymity VENDOR that can see commercial VPNs and proxies (proxycheck, IPinfo
     * Privacy, MaxMind Anonymous IP, or the anonymity traits of a MaxMind Insights call) answered for this IP
     * (EVAS-147). Only then is a `false` VPN/proxy/Tor verdict above a measurement.
     *
     * `false` covers the vendor timing out, refusing (proxycheck's free quota spent), a tier without a privacy
     * block, and `anonymityProvider: "none"`. The free corroboration lists never set it, even as the primary
     * provider: they see Tor exits, Apple's relay egress and cloud ranges, never a commercial VPN
     * (`packages/geoip/src/providers/free-lists.ts`). A `true` from them is still a real verdict.
     *
     * Absent on results stored before EVAS-147, which reads the same as `false`.
     */
    vendorChecked: z.boolean().optional(),
});
/** One lookup, one result — performed once per session in the join fn (fail-open, ~800 ms budget,
 * 24 h per-IP cache), persisted on the Session row and forwarded in dispatch metadata to fill the
 * LeadVal fingerprint's `transientMetadata.ipAddress` + `georesolver` (docs/leadval/02 §2C). */
export const GeoIpResult = z.object({
    ip: z.string(),
    geo: GeoIpGeo.default({}),
    network: GeoIpNetwork.default({}),
    anonymity: GeoIpAnonymity.default({
        isAnonymous: false,
        isVpn: false,
        isProxy: false,
        isTor: false,
        isRelay: false,
        isHosting: false,
        sources: [],
    }),
    provider: z.string(), // e.g. "geolite2-mmdb+proxycheck"
    lookedUpAt: z.string(), // ISO 8601
});
// ---------- providers ----------
export const GeoProviderId = z.enum([
    "geolite2-mmdb", // free MaxMind GeoLite2 City/ASN (attribution required)
    "geoip2-mmdb", // paid MaxMind DBs, same local reader
    "ipinfo-lite-mmdb", // free IPinfo Lite (country+ASN, daily, CC BY-SA)
    "ipinfo-api", // hosted IPinfo (Lite/Core/Max via one client)
    "maxmind-insights-api", // hosted GeoIP Insights (geo+ISP+anonymity in one call)
]);
export const AnonymityProviderId = z.enum([
    "proxycheck", // proxycheck.io (free 1k/day incl. operator names)
    "ipinfo-privacy", // rides the same ipinfo-api client (Core/Max)
    "maxmind-anonymous-mmdb", // Anonymous IP / Anonymous Plus local DBs
    "free-lists", // Tor exits + Apple Private Relay CSV + cloud ranges (corroboration-grade)
    "none",
]);
// ---------- presets + config (04 §11: three presets in the global super-admin UI) ----------
export const GeoIpPreset = z.enum(["free-only", "low-cost", "best-quality", "custom"]);
export const GeoIpConfig = z.object({
    preset: GeoIpPreset,
    geoProvider: GeoProviderId,
    anonymityProvider: AnonymityProviderId,
    /** Layer-3 corroboration lists (Tor/Apple/cloud ranges) on top of the anonymity provider. */
    corroborationLists: z.boolean().default(true),
    /** Secret Manager REFS per provider needing a key (never values). Key = provider id. */
    secretRefs: z.record(z.string(), z.string()).default({}),
});
/** A preset is sugar that pre-selects one geo + one anonymity provider; the per-provider selection
 * stays visible for `custom` combinations (04 §11). */
export const GEOIP_PRESETS = {
    "free-only": {
        geoProvider: "geolite2-mmdb",
        anonymityProvider: "proxycheck",
        corroborationLists: true,
    },
    "low-cost": {
        geoProvider: "ipinfo-api",
        anonymityProvider: "ipinfo-privacy",
        corroborationLists: true,
    },
    "best-quality": {
        geoProvider: "maxmind-insights-api",
        anonymityProvider: "maxmind-anonymous-mmdb",
        corroborationLists: true,
    },
};
/** The platform default (approved 2026-08-20: start free-only; upgrades are config, not code). */
export const GEOIP_DEFAULT_CONFIG = {
    preset: "free-only",
    ...GEOIP_PRESETS["free-only"],
    secretRefs: {},
};
/** Expand a preset into a full config (a `custom` preset returns the explicit selection as-is). */
export function geoIpConfigFromPreset(config) {
    if (config.preset === "custom")
        return config;
    return { ...config, ...GEOIP_PRESETS[config.preset] };
}
/** Global-vs-company resolution (04 §11, updated 2026-08-21): the per-company setting is UNSET by
 * default and unset means "inherit the global preset/provider selection"; when set it overrides for
 * that company's sessions. */
export function resolveGeoIpConfig(global, companyOverride) {
    return geoIpConfigFromPreset(companyOverride ?? global ?? GEOIP_DEFAULT_CONFIG);
}
/** The `PlatformConfig` key the global GeoIp settings row is stored under. */
export const GEOIP_PLATFORM_CONFIG_KEY = "geoip";
//# sourceMappingURL=geoip.js.map